import 'fomantic-ui-less/definitions/modules/progress.less';
import './styles/formFileAttachments.css';

import {SWF_FORM_PREFIX} from '../Common/constants';
import {fileAttachmentFieldNameAttribute} from '../Common/formFields';
import {fileUpload} from '../Common/fileUpload';
import {parseJSON} from '../Common/json';
import {richTextUploadUrlAttribute} from './workflowLaunchAttachments';

export const formFileUploadUrlAttribute = 'data-swf-file-upload-url';

const initializedAttribute = 'data-page-view-form-file-attachment';
const hiddenValueAttribute = 'data-swf-file-attachment-value';
const attachmentIdAttribute = 'data-swf-attachment-id';
const maxFilesAttributes = ['data-swf-max-files', 'data-max-files', 'max-files', 'maxfiles'];
const maxFilesLimit = 10;
const swfFilePrefix = 'swf:file:';
const fileIconClass = 'file outline icon';

if (typeof HTMLElement !== 'undefined') {
    require('@github/file-attachment-element');
}

const findAll = (root, selector) => [
    ...(root.matches?.(selector) ? [root] : []),
    ...(root.querySelectorAll?.(selector) || []),
];

const getAttributeValue = (element, attribute) => {
    const value = element?.getAttribute?.(attribute);
    return typeof value === 'string' ? value.trim() : '';
};

const closestAttributeValue = (element, attribute) =>
    getAttributeValue(element, attribute) ||
    getAttributeValue(element?.closest?.(`[${attribute}]`), attribute);

const formElement = fileAttachment => fileAttachment.closest?.('form');

const documentFor = element => element.ownerDocument || document;

const inputById = (fileAttachment, inputId) =>
    findAll(fileAttachment, 'input[type="file"]').find(input => input.id === inputId);

const fileInput = fileAttachment => {
    const inputId = getAttributeValue(fileAttachment, 'input');
    return inputById(fileAttachment, inputId) || fileAttachment.querySelector?.('input[type="file"]');
};

const contextValue = (fileAttachment, id) =>
    findAll(formElement(fileAttachment) || fileAttachment, 'input')
        .find(input => input.id === id)
        ?.value
        ?.trim() || '';

const pageId = fileAttachment =>
    contextValue(fileAttachment, 'swf:pageId') ||
    documentFor(fileAttachment).querySelector?.('meta[name="swf-page-id"]')?.getAttribute('content')?.trim() ||
    '';

const defaultUploadUrl = (fileAttachment, location) => {
    const workflowId = contextValue(fileAttachment, 'swf:workflowId');
    const taskId = contextValue(fileAttachment, 'swf:taskId');

    if (workflowId && taskId) {
        return `/workflows/${workflowId}/tasks/${taskId}/attachments`;
    }

    const currentPageId = pageId(fileAttachment);
    if (currentPageId) {
        return `/pages/${currentPageId}/attachments`;
    }

    return `${new URL(location.href).pathname}/attachments`;
};

export const resolveFormFileAttachmentUploadUrl = (fileAttachment, location = window.location) =>
    closestAttributeValue(fileAttachment, formFileUploadUrlAttribute) ||
    closestAttributeValue(fileAttachment, richTextUploadUrlAttribute) ||
    defaultUploadUrl(fileAttachment, location);

const maxFiles = (fileAttachment, input) => {
    const configuredValue = maxFilesAttributes
        .map(attribute => getAttributeValue(fileAttachment, attribute) || getAttributeValue(input, attribute))
        .find(Boolean);
    const configuredMaxFiles = Number.parseInt(configuredValue, 10);
    if (!Number.isFinite(configuredMaxFiles)) {
        return 1;
    }

    return Math.min(maxFilesLimit, Math.max(1, configuredMaxFiles));
};

const acceptTokens = (fileAttachment, input) =>
    (getAttributeValue(fileAttachment, 'accept') || getAttributeValue(input, 'accept'))
        .split(',')
        .map(token => token.trim().toLowerCase())
        .filter(Boolean);

const defaultFileAttachmentLabel = 'Paste, drop, or click to add files';

const fieldLabelText = existingLabelText => existingLabelText || defaultFileAttachmentLabel;

export const fileMatchesAccept = (file, accept) => {
    if (accept.length === 0) {
        return true;
    }

    const fileName = (file.name || '').toLowerCase();
    const fileType = (file.type || '').toLowerCase();

    return accept.some(token => {
        if (token.startsWith('.')) {
            return fileName.endsWith(token);
        }

        if (token.endsWith('/*')) {
            return fileType.startsWith(token.substring(0, token.length - 1));
        }

        return fileType === token;
    });
};

const fieldName = input => {
    const existingFieldName = getAttributeValue(input, fileAttachmentFieldNameAttribute);
    if (existingFieldName) {
        return existingFieldName;
    }

    const name = getAttributeValue(input, 'name') || getAttributeValue(input, 'id');
    if (name) {
        input.setAttribute(fileAttachmentFieldNameAttribute, name);
    }
    input.removeAttribute?.('name');
    return name;
};

const ensureMessage = fileAttachment => {
    const existingMessage = fileAttachment.querySelector?.('.ui.error.message');
    if (existingMessage) {
        return existingMessage;
    }

    const message = documentFor(fileAttachment).createElement('div');
    message.className = 'ui error message hidden';
    fileAttachment.insertBefore?.(message, fileAttachment.firstChild);
    if (!message.parentElement) {
        fileAttachment.appendChild(message);
    }
    return message;
};

const setError = (fileAttachment, messageText) => {
    const message = ensureMessage(fileAttachment);
    message.textContent = messageText;
    message.classList.remove('hidden');
    message.classList.add('visible');
    fileAttachment.closest?.('.field')?.classList.add('error');
};

const clearError = fileAttachment => {
    const message = fileAttachment.querySelector?.('.ui.error.message');
    if (message) {
        message.textContent = '';
        message.classList.add('hidden');
        message.classList.remove('visible');
    }
    fileAttachment.closest?.('.field')?.classList.remove('error');
};

const ensureProgress = fileAttachment => {
    const existingProgress = fileAttachment.querySelector?.('.ui.progress');
    if (existingProgress) {
        return existingProgress;
    }

    const progress = documentFor(fileAttachment).createElement('div');
    progress.className = 'ui small indicating progress';
    progress.style.display = 'none';
    progress.innerHTML = '<div class="bar"><div class="progress"></div></div>';
    fileAttachment.appendChild(progress);
    return progress;
};

const setProgress = (fileAttachment, percent) => {
    const progress = ensureProgress(fileAttachment);
    progress.style.display = 'block';
    const clampedPercent = Math.max(0, Math.min(100, percent));
    const bar = progress.querySelector?.('.bar');
    const text = progress.querySelector?.('.progress');
    if (bar) {
        bar.style.width = `${clampedPercent}%`;
    }
    if (text) {
        text.textContent = `${Math.round(clampedPercent)}%`;
    }
};

const hideProgress = fileAttachment => {
    const progress = fileAttachment.querySelector?.('.ui.progress');
    if (progress) {
        progress.style.display = 'none';
    }
};

const ensureList = fileAttachment => {
    const existingList = fileAttachment.querySelector?.('.swf-file-attachment-list');
    if (existingList) {
        return existingList;
    }

    const list = documentFor(fileAttachment).createElement('div');
    list.className = 'swf-file-attachment-list ui middle aligned divided list';
    fileAttachment.appendChild(list);
    return list;
};

const ensureTrigger = state => {
    const fileAttachment = state.fileAttachment;
    const existingTrigger = fileAttachment.querySelector?.('.swf-file-attachment-trigger');
    const existingLabelText = existingTrigger
        ?.querySelector?.('span')
        ?.textContent
        ?.trim() || '';
    fileAttachment.querySelectorAll?.('.swf-file-attachment-placeholder').forEach(placeholder =>
        placeholder.remove?.());
    existingTrigger?.remove?.();

    const trigger = existingTrigger || documentFor(fileAttachment).createElement('label');
    trigger.className = 'swf-file-attachment-trigger ui icon header';
    const inputId = getAttributeValue(state.input, 'id');
    if (inputId) {
        trigger.setAttribute('for', inputId);
    }
    trigger.innerHTML = '';

    const label = documentFor(fileAttachment).createElement('span');
    label.className = 'ui button';

    const icon = documentFor(fileAttachment).createElement('i');
    icon.className = fileIconClass;
    icon.setAttribute('aria-hidden', 'true');
    label.appendChild(icon);
    label.appendChild(documentFor(fileAttachment).createTextNode(fieldLabelText(existingLabelText)));

    trigger.appendChild(label);
    fileAttachment.insertBefore?.(trigger, state.input);
    if (!trigger.parentElement) {
        fileAttachment.appendChild(trigger);
    }

    return trigger;
};

const removeHiddenValues = fileAttachment => {
    findAll(fileAttachment, `input[${hiddenValueAttribute}]`).forEach(input => input.remove());
};

const renderHiddenValues = (fileAttachment, name, entries) => {
    removeHiddenValues(fileAttachment);
    entries.forEach(entry => {
        const input = documentFor(fileAttachment).createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = entry.reference;
        input.setAttribute(hiddenValueAttribute, name);
        input.setAttribute(attachmentIdAttribute, entry.id);
        fileAttachment.appendChild(input);
    });
};

const renderList = (fileAttachment, state) => {
    const list = ensureList(fileAttachment);
    list.innerHTML = '';

    state.entries.forEach(entry => {
        const item = documentFor(fileAttachment).createElement('div');
        item.className = 'item';

        const content = documentFor(fileAttachment).createElement('div');
        content.className = 'content';

        const icon = documentFor(fileAttachment).createElement('i');
        icon.className = fileIconClass;
        icon.setAttribute('aria-hidden', 'true');
        content.appendChild(icon);

        const label = documentFor(fileAttachment).createElement('span');
        label.textContent = entry.name || entry.id;
        content.appendChild(label);

        const removeButton = documentFor(fileAttachment).createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'ui mini basic button right floated';
        removeButton.textContent = 'Remove';
        removeButton.addEventListener('click', event => {
            event.preventDefault();
            state.entries = state.entries.filter(currentEntry => currentEntry.id !== entry.id);
            renderState(state);
        });

        item.appendChild(removeButton);
        item.appendChild(content);
        list.appendChild(item);
    });
};

const responseAttachmentId = response => {
    if (typeof response?.json === 'string') {
        return response.json;
    }

    if (typeof response?.text === 'string') {
        const parsedText = parseJSON(response.text);
        return typeof parsedText === 'string' ? parsedText : response.text.replace(/^"|"$/g, '');
    }

    return response?.json?.id || response?.json?.objectReferenceId || response?.id || '';
};

const responseErrorMessage = response => {
    if (response?.json?.message) {
        return response.json.message;
    }

    if (response?.message) {
        return response.message;
    }

    const parsedText = parseJSON(response?.text);
    return parsedText?.message || response?.text || 'Unable to upload file.';
};

const uploadAttachment = (state, attachment) => {
    const file = attachment.file || attachment;
    return state.fileUpload(state.uploadUrl, file, event => {
        if (event.total) {
            const percent = event.loaded / event.total * 100;
            attachment.saving?.(percent);
            setProgress(state.fileAttachment, percent);
        }
    }).then(response => {
        if (response.status !== 200) {
            throw new Error(responseErrorMessage(response));
        }

        const id = responseAttachmentId(response);
        if (!id) {
            throw new Error('The upload response did not include an attachment id.');
        }

        attachment.saved?.({
            id,
            name: file.name,
        });

        return {
            id,
            name: attachment.name || file.name,
            reference: `${swfFilePrefix}${id}`,
        };
    });
};

const fileReferenceEntries = values => {
    const submittedValues = Array.isArray(values) ? values : [values];
    return submittedValues
        .filter(value => typeof value === 'string' && value.startsWith(swfFilePrefix))
        .map(reference => {
            const id = reference.substring(swfFilePrefix.length);
            return {
                id,
                name: id,
                reference,
            };
        });
};

const renderState = state => {
    ensureTrigger(state);
    renderHiddenValues(state.fileAttachment, state.fieldName, state.entries);
    renderList(state.fileAttachment, state);
    syncUploadAvailability(state);
};

const clearEntries = state => {
    state.entries = [];
    renderState(state);
};

export const pendingFormFileAttachmentUploads = root => {
    const effectiveRoot = root || (typeof document !== 'undefined' ? document : null);
    if (!effectiveRoot) {
        return [];
    }

    return findAll(effectiveRoot, 'file-attachment')
        .flatMap(fileAttachment => {
            const state = fileAttachment.__swfFormFileAttachment;
            if (state?.pendingUploads) {
                return [...state.pendingUploads];
            }
            return state?.pending ? [state.pending] : [];
        })
        .filter(pending => pending && typeof pending.then === 'function');
};

export const waitForFormFileAttachmentUploads = root =>
    Promise.all(pendingFormFileAttachmentUploads(root));

export const validateRequiredFormFileAttachments = (formElement, formFields) => {
    let valid = true;
    findAll(formElement, 'file-attachment')
        .map(fileAttachment => fileAttachment.__swfFormFileAttachment)
        .filter(state => state?.fieldName)
        .forEach(state => {
            const fieldName = state.fieldName.startsWith(SWF_FORM_PREFIX)
                ? state.fieldName.substring(SWF_FORM_PREFIX.length)
                : state.fieldName;
            const formField = formFields?.[fieldName];
            const visible = state.fileAttachment?.closest?.('.field')?.style?.display !== 'none';
            if (!formField?.fileAttachment || formField.optional || !visible) {
                state.clearError?.();
            } else if ((state.entries || []).length > 0) {
                state.clearError?.();
            } else {
                state.showError?.(`Upload at least one file for ${fieldName}.`);
                valid = false;
            }
        });
    return valid;
};

const uploadAttachments = async (state, attachments) => {
    const uploadedEntries = [];
    for (const attachment of attachments) {
        const entry = await uploadAttachment(state, attachment);
        uploadedEntries.push(entry);
    }
    return uploadedEntries;
};

const syncUploadAvailability = state => {
    const disabled = state.uploading || state.entries.length >= state.maxFiles;
    state.input.disabled = disabled;
    state.fileAttachment.classList.toggle('disabled', disabled);
    state.fileAttachment.querySelectorAll?.('.swf-file-attachment-trigger').forEach(trigger => {
        trigger.classList.toggle('disabled', disabled);
    });
};

const acceptedAttachments = event =>
    (event.detail?.attachments || []).filter(attachment => attachment?.file || attachment);

const attachmentFiles = attachments =>
    attachments.map(attachment => attachment.file || attachment);

const validateFiles = (state, files) => {
    if (files.length === 0) {
        return null;
    }

    const remainingFiles = state.maxFiles - state.entries.length;
    if (remainingFiles <= 0 || files.length > remainingFiles) {
        return `Upload no more than ${state.maxFiles} ${state.maxFiles === 1 ? 'file' : 'files'}.`;
    }

    const invalidFile = files.find(file => !fileMatchesAccept(file, state.accept));
    if (invalidFile) {
        return `${invalidFile.name || 'The selected file'} is not an allowed file type.`;
    }

    return null;
};

const acceptAttachments = (state, event) => {
    const attachments = acceptedAttachments(event);
    const validationError = validateFiles(state, attachmentFiles(attachments));
    if (!validationError) {
        clearError(state.fileAttachment);
        return;
    }

    event.preventDefault?.();
    setError(state.fileAttachment, validationError);
};

const handleAcceptedAttachments = async (state, attachments) => {
    if (attachments.length === 0) {
        return;
    }

    clearError(state.fileAttachment);
    setProgress(state.fileAttachment, 0);
    state.uploading = true;
    state.input.disabled = true;
    const version = state.version;

    try {
        const uploadedEntries = await uploadAttachments(state, attachments);
        if (state.version !== version) {
            return;
        }

        state.entries = [...state.entries, ...uploadedEntries].slice(0, state.maxFiles);
        renderState(state);
    } catch (error) {
        setError(state.fileAttachment, error.message || 'Unable to upload file.');
        throw error;
    } finally {
        state.uploading = false;
        renderState(state);
        hideProgress(state.fileAttachment);
    }
};

const setupFormFileAttachment = (fileAttachment, options) => {
    if (fileAttachment.hasAttribute?.(initializedAttribute)) {
        return;
    }

    const input = fileInput(fileAttachment);
    if (!input) {
        return;
    }

    const name = fieldName(input);
    if (!name) {
        return;
    }

    fileAttachment.setAttribute?.(initializedAttribute, 'true');

    const state = {
        accept: acceptTokens(fileAttachment, input),
        entries: [],
        fieldName: name,
        fileAttachment,
        fileUpload: options.fileUpload || fileUpload,
        input,
        location: options.location || window.location,
        maxFiles: maxFiles(fileAttachment, input),
        pending: null,
        pendingUploads: new Set(),
        uploading: false,
        version: 0,
        clearValues: () => {
            state.version += 1;
            clearEntries(state);
        },
        clearError: () => clearError(fileAttachment),
        showError: message => setError(fileAttachment, message),
        setValues: values => {
            state.version += 1;
            state.entries = fileReferenceEntries(values).slice(0, state.maxFiles);
            renderState(state);
        },
        uploadUrl: resolveFormFileAttachmentUploadUrl(fileAttachment, options.location || window.location),
    };
    fileAttachment.__swfFormFileAttachment = state;
    input.toggleAttribute('multiple', state.maxFiles > 1);

    ensureMessage(fileAttachment);
    ensureProgress(fileAttachment);
    ensureList(fileAttachment);
    renderState(state);

    fileAttachment.addEventListener('file-attachment-accept', event =>
        acceptAttachments(state, event));

    fileAttachment.addEventListener('file-attachment-accepted', event => {
        event.preventDefault?.();
        const attachments = acceptedAttachments(event);
        const pending = handleAcceptedAttachments(state, attachments);
        state.pending = pending;
        state.pendingUploads.add(pending);
        pending.finally(() => {
            state.pendingUploads.delete(pending);
            if (state.pending === pending) {
                state.pending = null;
            }
        }).catch(() => {});
    });
};

export const setupFormFileAttachments = (root = document, options = {}) => {
    findAll(root, 'file-attachment').forEach(fileAttachment =>
        setupFormFileAttachment(fileAttachment, options));
};
